<template>
  <div class="mobile-survey">
    <!-- Loading state -->
    <div v-if="isLoading" class="loading">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading...</p>
    </div>

    <!-- Invalid or blank (no hashId) -->
    <div v-else-if="!isValid" class="blank"></div>

    <!-- Already responded message -->
    <div v-else-if="alreadyResponded" class="message-container">
      <div class="icon">
        <i class="fas fa-check-circle"></i>
      </div>
      <h2>Already Submitted</h2>
      <p>You have already responded to this survey.</p>
      <p class="contact-info">
        Please contact<br />
        <a href="mailto:communications@bigpropertymanagement.com">
          communications@bigpropertymanagement.com
        </a>
        for help.
      </p>
    </div>

    <!-- Thank you message -->
    <div v-else-if="showThankYou" class="message-container">
      <div class="icon success">
        <i class="fas fa-check-circle"></i>
      </div>
      <h2>Thank You!</h2>
      <p>Thank you for your response.</p>
      <p class="contact-info">
        Please contact
        <a href="mailto:communications@bigpropertymanagement.com">
          communications@bigpropertymanagement.com
        </a>
        for any follow-up questions.
      </p>
    </div>

    <!-- Survey form -->
    <div v-else class="form-container">
      <!-- Fixed Header -->
      <div class="form-header">
        <div class="title-bar">
          <h2>{{ campaignTitle || 'Survey' }}</h2>
        </div>
        <p v-if="campaignDescription" class="campaign-description">
          {{ campaignDescription }}
        </p>
      </div>

      <!-- Scrollable Content -->
      <div class="form-content">
        <!-- Name/Phone/Email Entry -->
        <div v-if="!surveyStarted" class="entry-section">
          <div class="form-row">
            <div class="form-group half-width">
              <input
                v-model="formData.firstName"
                type="text"
                placeholder="First Name"
                required
                maxlength="100"
                :class="{ error: errors.firstName }"
                @input="clearError('firstName')"
              />
              <span v-if="errors.firstName" class="error-text">{{
                errors.firstName
              }}</span>
            </div>

            <div class="form-group half-width">
              <input
                v-model="formData.lastName"
                type="text"
                placeholder="Last Name"
                required
                maxlength="100"
                :class="{ error: errors.lastName }"
                @input="clearError('lastName')"
              />
              <span v-if="errors.lastName" class="error-text">{{
                errors.lastName
              }}</span>
            </div>
          </div>

          <div class="form-group">
            <input
              v-model="formData.phone"
              type="tel"
              placeholder="Phone (10 digits)"
              required
              maxlength="10"
              :class="{ error: errors.phone }"
              @input="clearError('phone')"
            />
            <span v-if="errors.phone" class="error-text">{{
              errors.phone
            }}</span>
          </div>

          <div class="form-group">
            <input
              v-model="formData.email"
              type="email"
              placeholder="Email (optional)"
              maxlength="255"
              :class="{ error: errors.email }"
              @input="clearError('email')"
            />
            <span v-if="errors.email" class="error-text">{{
              errors.email
            }}</span>
          </div>

          <button @click="beginSurvey" class="btn-begin">Begin</button>
        </div>

        <!-- Questions Section -->
        <div v-else class="questions-section">
          <div v-if="questions.length > 0" class="question-container">
            <label class="question-label">
              {{ currentQuestionIndex + 1 }}. {{ currentQuestion.text }}
            </label>

            <!-- Freeform text answer -->
            <textarea
              v-if="currentQuestion.type === 'freeform'"
              ref="freeformInput"
              v-model="answers[currentQuestion.id]"
              class="answer-textarea"
              rows="8"
              placeholder="Enter your answer..."
            ></textarea>

            <!-- Yes/No answer -->
            <div
              v-else-if="currentQuestion.type === 'yesno'"
              class="yesno-buttons"
            >
              <button
                @click="answers[currentQuestion.id] = 'Yes'"
                :class="{ active: answers[currentQuestion.id] === 'Yes' }"
                class="btn-yesno"
              >
                Yes
              </button>
              <button
                @click="answers[currentQuestion.id] = 'No'"
                :class="{ active: answers[currentQuestion.id] === 'No' }"
                class="btn-yesno"
              >
                No
              </button>
            </div>

            <!-- Multiple choice answer -->
            <div
              v-else-if="currentQuestion.type === 'multiple_choice'"
              class="multiple-choice"
            >
              <div
                v-if="
                  !currentQuestion.options ||
                  currentQuestion.options.length === 0
                "
                class="error-message"
              >
                No options available for this question
              </div>
              <div
                v-for="(option, index) in currentQuestion.options"
                :key="index"
                class="choice-option"
              >
                <input
                  :id="`option-${index}`"
                  v-model="answers[currentQuestion.id]"
                  type="radio"
                  :value="option"
                  :name="`question-${currentQuestion.id}`"
                />
                <label :for="`option-${index}`">{{ option }}</label>
              </div>
            </div>

            <!-- Final question completion message -->
            <div
              v-if="
                currentQuestionIndex === questions.length - 1 &&
                answers[currentQuestion.id]
              "
              class="completion-message"
            >
              You're all done! Please click Submit or go back to edit your
              answers.
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Navigation (moved outside scrollable content) -->
      <div v-if="surveyStarted && questions.length > 0" class="form-navigation">
        <button
          @click="previousQuestion"
          class="btn-nav"
          :disabled="currentQuestionIndex === 0 && !canReturnToEntry"
        >
          PREV
        </button>
        <span class="question-counter">
          {{ currentQuestionIndex + 1 }} of {{ questions.length }}
        </span>
        <button
          @click="nextQuestion"
          class="btn-nav"
          :disabled="currentQuestionIndex === questions.length - 1"
        >
          NEXT
        </button>
      </div>

      <!-- Fixed Submit Button -->
      <div class="form-footer">
        <button @click="handleSubmit" :disabled="!canSubmit" class="btn-submit">
          Submit
        </button>
        <div v-if="submitError" class="error-message">{{ submitError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';

export default {
  name: 'MobileSurvey',
  data() {
    return {
      isLoading: true,
      isValid: false,
      alreadyResponded: false,
      showThankYou: false,
      hashId: null,
      campaignTitle: '',
      campaignDescription: '',
      questions: [],
      formData: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
      answers: {},
      errors: {},
      submitError: '',
      surveyStarted: false,
      currentQuestionIndex: 0,
      canReturnToEntry: true,
    };
  },
  computed: {
    currentQuestion() {
      if (
        this.questions.length > 0 &&
        this.currentQuestionIndex < this.questions.length
      ) {
        return this.questions[this.currentQuestionIndex];
      }
      return null;
    },
    canSubmit() {
      // Can submit if survey started and all questions have been answered
      if (!this.surveyStarted) return false;
      if (this.questions.length === 0) return false;

      for (const question of this.questions) {
        if (
          !this.answers[question.id] ||
          this.answers[question.id].trim() === ''
        ) {
          return false;
        }
      }
      return true;
    },
  },
  async mounted() {
    await this.validateCampaign();
  },
  methods: {
    getBrowserInfo() {
      const ua = navigator.userAgent;
      let browser = 'Unknown';

      if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
      else if (ua.includes('Safari') && !ua.includes('Chrome'))
        browser = 'Safari';
      else if (ua.includes('Firefox')) browser = 'Firefox';
      else if (ua.includes('Edg')) browser = 'Edge';
      else if (ua.includes('MSIE') || ua.includes('Trident')) browser = 'IE';

      return browser;
    },

    getDeviceType() {
      const ua = navigator.userAgent;

      if (/mobile/i.test(ua)) {
        if (/iPad/.test(ua)) return 'iPad';
        if (/iPhone/.test(ua)) return 'iPhone';
        if (/Android/.test(ua)) return 'Android Mobile';
        return 'Mobile';
      }

      if (/tablet|iPad/i.test(ua)) return 'Tablet';

      return 'Desktop';
    },

    getScreenResolution() {
      return `${window.screen.width}x${window.screen.height}`;
    },

    async validateCampaign() {
      this.isLoading = true;

      // Get hash from query params
      const urlParams = new URLSearchParams(window.location.search);
      this.hashId = urlParams.get('id');

      if (!this.hashId) {
        this.isValid = false;
        this.isLoading = false;
        return;
      }

      try {
        const response = await axios.get(
          `/api/public/survey/validate/${this.hashId}`,
          {
            params: {
              phone: this.formData.phone || null,
              browser: this.getBrowserInfo(),
              device: this.getDeviceType(),
              screenResolution: this.getScreenResolution(),
            },
          },
        );

        if (response.data && response.data.valid) {
          this.isValid = true;
          this.campaignTitle = response.data.campaign.title;
          this.campaignDescription = response.data.campaign.description;
          this.questions = response.data.campaign.questions || [];
          console.log('Loaded questions:', this.questions);
        } else {
          this.isValid = false;
        }
      } catch (error) {
        console.error('Error validating survey:', error);
        this.isValid = false;
      } finally {
        this.isLoading = false;
      }
    },

    clearError(field) {
      this.errors[field] = '';
    },

    validateEntry() {
      this.errors = {};
      let valid = true;

      // Validate first name
      const nameCheck = /^[A-Za-z\s'-]+$/;
      if (!this.formData.firstName || this.formData.firstName.trim() === '') {
        this.errors.firstName = 'First name is required';
        valid = false;
      } else if (!nameCheck.test(this.formData.firstName)) {
        this.errors.firstName = 'Invalid first name';
        valid = false;
      }

      // Validate last name
      if (!this.formData.lastName || this.formData.lastName.trim() === '') {
        this.errors.lastName = 'Last name is required';
        valid = false;
      } else if (!nameCheck.test(this.formData.lastName)) {
        this.errors.lastName = 'Invalid last name';
        valid = false;
      }

      // Validate phone
      const phoneCheck = /^\d{10}$/;
      if (!this.formData.phone || this.formData.phone.trim() === '') {
        this.errors.phone = 'Phone is required';
        valid = false;
      } else if (!phoneCheck.test(this.formData.phone)) {
        this.errors.phone = 'Phone must be 10 digits';
        valid = false;
      }

      // Validate email if provided
      if (this.formData.email && this.formData.email.trim() !== '') {
        const emailCheck = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailCheck.test(this.formData.email)) {
          this.errors.email = 'Invalid email format';
          valid = false;
        }
      }

      return valid;
    },

    async beginSurvey() {
      if (!this.validateEntry()) {
        return;
      }

      // Check if phone has already responded
      try {
        const response = await axios.get(
          `/api/public/survey/validate/${this.hashId}`,
          {
            params: {
              phone: this.formData.phone,
              browser: this.getBrowserInfo(),
              device: this.getDeviceType(),
              screenResolution: this.getScreenResolution(),
            },
          },
        );

        if (response.data && !response.data.valid) {
          this.alreadyResponded = true;
          return;
        }
      } catch (error) {
        console.error('Error checking phone:', error);
        this.submitError = 'Error validating phone number';
        return;
      }

      this.surveyStarted = true;
      this.currentQuestionIndex = 0;
      this.$nextTick(() => {
        this.focusCurrentQuestion();
      });
    },

    focusCurrentQuestion() {
      // Focus on freeform textarea if it exists
      if (this.$refs.freeformInput) {
        this.$refs.freeformInput.focus();
      }
    },

    previousQuestion() {
      if (this.currentQuestionIndex === 0 && this.canReturnToEntry) {
        // Return to name/phone/email entry
        this.surveyStarted = false;
      } else if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
        this.$nextTick(() => {
          this.focusCurrentQuestion();
        });
      }
    },

    nextQuestion() {
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        this.$nextTick(() => {
          this.focusCurrentQuestion();
        });
      }
    },

    async handleSubmit() {
      if (!this.canSubmit) {
        this.submitError = 'Please answer all questions';
        return;
      }

      this.submitError = '';

      try {
        // Build answers array
        const answersArray = this.questions.map((q) => ({
          questionId: q.id,
          answerText: this.answers[q.id] || '',
        }));

        const payload = {
          hashId: this.hashId,
          firstName: this.formData.firstName,
          lastName: this.formData.lastName,
          phone: this.formData.phone,
          email: this.formData.email || null,
          answers: answersArray,
        };

        const response = await axios.post('/api/public/survey/submit', payload);

        if (response.data && response.data.success) {
          this.showThankYou = true;
        } else {
          this.submitError = 'Error submitting survey';
        }
      } catch (error) {
        console.error('Error submitting survey:', error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          this.submitError = error.response.data.error;
        } else {
          this.submitError = 'Error submitting survey';
        }
      }
    },
  },
};
</script>

<style scoped>
.mobile-survey {
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
  padding: 20px;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu,
    Cantarell, sans-serif;
  display: flex;
  align-items: top;
  justify-content: center;
  background-color: #f5f5f5;
  max-width: 440px;
}

/* Loading State */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  color: #333;
}

.loading i {
  font-size: 3em;
  margin-bottom: 1em;
  color: #a080a8;
}

/* Blank State */
.blank {
  height: 100vh;
  background-color: #f5f5f5;
}

/* Message Container */
.message-container {
  max-width: 480px;
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 40px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  overflow: hidden;
}

.message-container .icon {
  font-size: 4em;
  margin-bottom: 0.5em;
  color: #ffc107;
}

.message-container .icon.success {
  color: #28a745;
}

.message-container h2 {
  margin: 0 0 0.5em 0;
  font-size: 1.8em;
  color: #333;
}

.message-container p {
  margin: 0.5em 0;
  font-size: 1.1em;
  color: #666;
}

.contact-info {
  margin-top: 1.5em;
  font-size: 0.95em;
  color: #888;
}

.contact-info a {
  color: #a080a8;
  text-decoration: none;
}

/* Form Container */
.form-container {
  max-width: 480px;
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 40px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Fixed Header */
.form-header {
  flex-shrink: 0;
  padding: 0;
  background: white;
  border-bottom: 1px solid #e0e0e0;
}

.title-bar {
  background: #a080a8;
  border-radius: 16px 16px 0 0;
  padding: 0;
  margin: 0;
}

.title-bar h2 {
  margin: 0;
  padding: 20px 30px;
  color: white;
  font-size: 1.8em;
  text-align: center;
}

.campaign-description {
  padding: 8px 30px 4px 30px;
  text-align: left;
  color: #666;
  margin: 0;
  background: white;
  font-size: 0.85em;
}

/* Scrollable Content */
.form-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #fff;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.form-content::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* Entry Section */
.entry-section {
  max-width: 420px;
  margin: 0 auto;
}

.form-row {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group.half-width {
  flex: 1;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-group input.error {
  border-color: #dc3545;
}

.error-text {
  color: #dc3545;
  font-size: 0.85em;
  margin-top: 5px;
  display: block;
}

.btn-begin {
  width: 100%;
  padding: 15px;
  font-size: 1.1em;
  font-weight: bold;
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
}

.btn-begin:hover {
  background: #8a6a8f;
}

/* Questions Section */
.questions-section {
  max-width: 420px;
  margin: 0 auto;
}

.question-container {
  margin-bottom: 20px;
}

.question-label {
  display: block;
  font-size: 1.1em;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.4;
}

.answer-textarea {
  width: 100%;
  padding: 12px;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  height: 170px;
}

/* Yes/No Buttons */
.yesno-buttons {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.btn-yesno {
  flex: 1;
  padding: 15px;
  font-size: 1.1em;
  font-weight: bold;
  background-color: #e9ecef;
  color: #333;
  border: 2px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-yesno:hover {
  background-color: #dee2e6;
}

.btn-yesno.active {
  background: #a080a8;
  color: white;
  border-color: #a080a8;
}

/* Multiple Choice */
.multiple-choice {
  margin-bottom: 20px;
}

.choice-option {
  display: flex;
  align-items: center;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.choice-option:hover {
  background-color: #f8f9fa;
}

.choice-option input[type='radio'] {
  margin-right: 10px;
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.choice-option label {
  flex: 1;
  cursor: pointer;
  font-size: 1em;
  color: #333;
}

/* Fixed Navigation */
.form-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background-color: #fff;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.btn-nav {
  padding: 10px 20px;
  font-size: 1em;
  font-weight: bold;
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  min-width: 80px;
}

.btn-nav:hover:not(:disabled) {
  background: #8a6a8f;
}

.btn-nav:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.5;
}

.question-counter {
  font-size: 1em;
  font-weight: 600;
  color: #666;
}

/* Fixed Footer */
.form-footer {
  background-color: #fff;
  padding: 15px 20px;
  border-top: 1px solid #ddd;
  flex-shrink: 0;
}

.btn-submit {
  width: 100%;
  padding: 15px;
  font-size: 1.2em;
  font-weight: bold;
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-submit:hover:not(:disabled) {
  background: #8a6a8f;
}

.btn-submit:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.error-message {
  color: #dc3545;
  text-align: center;
  margin-top: 10px;
  font-size: 0.95em;
}

.completion-message {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 15px;
  border-radius: 4px;
  margin-top: 20px;
  text-align: center;
  font-size: 1em;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .mobile-survey {
    padding: 10px;
  }

  .form-container,
  .message-container {
    max-height: calc(79vh - 20px);
  }

  .form-header {
    padding: 0;
  }

  .title-bar h2 {
    padding: 15px 20px;
    font-size: 1.5em;
  }

  .form-content {
    padding: 15px 20px 20px 20px;
  }

  .form-footer {
    padding: 15px 20px;
  }

  h2 {
    font-size: 1.5em;
  }

  input,
  textarea {
    font-size: 16px; /* Prevent zoom on iOS */
  }
}

/* Screens 810px height and lower - use 90vh */
@media (min-width: 360px) and (max-height: 810px) and (max-width: 480px) {
  .form-container,
  .message-container {
    max-height: calc(90vh - 20px);
  }
}

/* Screens 915px height and larger - use 76vh */
@media (min-width: 360px) and (min-height: 915px) and (max-width: 480px) {
  .form-container,
  .message-container {
    max-height: calc(76vh - 20px);
  }
}
</style>
